import { useState, useEffect } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { MessageCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import Link from 'next/link';
import type { AssistantDisplay } from '@/types';
import { RelativeTime } from '@/components/RelativeTime';
import FirebaseService from '@/lib/firebase';
import { useToast } from "@/components/hooks/use-toast";
import { SidebarGroup, SidebarGroupLabel, SidebarGroupContent, SidebarMenu, SidebarMenuItem, SidebarMenuButton } from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Message } from '@/lib/firebase';

interface EmissaryListMinimalProps {
  userId?: string;
  maxItems?: number;
  isCollapsible?: boolean;
}

export function EmissaryListMinimal({ 
  userId, 
  maxItems = 5,
  isCollapsible = true 
}: EmissaryListMinimalProps) {
  const [assistants, setAssistants] = useState<AssistantDisplay[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [lastMessages, setLastMessages] = useState<Record<string, Message>>({});
  const { toast } = useToast();

  useEffect(() => {
    const loadRecentAssistants = async () => {
      if (!userId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Get user's assistants
        const userAssistants = await FirebaseService.getUserAssistants(userId);
        
        // Sort by last accessed date, most recent first
        const sortedAssistants = userAssistants
          .sort((a, b) => {
            const aTime = a.lastAccessedAt ? new Date(a.lastAccessedAt).getTime() : 0;
            const bTime = b.lastAccessedAt ? new Date(b.lastAccessedAt).getTime() : 0;
            return bTime - aTime;
          })
          .map(assistant => ({
            assistantId: assistant.assistantId,
            templateId: assistant.templateId,
            name: assistant.name,
            avatar: assistant.avatar,
            ownerId: assistant.ownerId,
            ownerEmail: assistant.ownerEmail,
            initialMessage: assistant.initialMessage,
            initialInstructions: assistant.initialInstructions,
            initialDocuments: assistant.initialDocuments || [],
            createdAt: assistant.createdAt ? new Date(assistant.createdAt) : undefined,
            updatedAt: assistant.updatedAt ? new Date(assistant.updatedAt) : undefined,
            shareUrl: `/assistant/${assistant.assistantId}`,
            usageCount: assistant.usageCount || 0,
            lastAccessedAt: assistant.lastAccessedAt ? new Date(assistant.lastAccessedAt) : undefined,
            tools: assistant.tools || [],
            toolConfigs: assistant.toolConfigs || {},
            accessControl: assistant.accessControl || { type: 'domain' }
          } as AssistantDisplay));

        setAssistants(sortedAssistants);

        // Fetch last message for each assistant
        const messagePromises = sortedAssistants.map(async (assistant) => {
          try {
            const messages = await FirebaseService.getMessages(assistant.assistantId);
            if (messages.length > 0) {
              // Get the last message (most recent)
              const lastMessage = messages[messages.length - 1];
              return { assistantId: assistant.assistantId, message: lastMessage };
            }
            return null;
          } catch (error) {
            console.error(`Error fetching messages for assistant ${assistant.assistantId}:`, error);
            return null;
          }
        });

        const messageResults = await Promise.all(messagePromises);
        const messageMap: Record<string, Message> = {};
        
        messageResults.forEach(result => {
          if (result) {
            messageMap[result.assistantId] = result.message;
          }
        });
        
        setLastMessages(messageMap);
      } catch (error) {
        console.error('Error loading recent assistants:', error);
        toast({
          title: "Error Loading Assistants",
          description: "Failed to load recent assistants",
          variant: "destructive"
        });
        setAssistants([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadRecentAssistants();
  }, [userId, toast]);

  const displayedAssistants = isExpanded ? assistants : assistants.slice(0, maxItems);
  const hasMore = assistants.length > maxItems;

  if (isLoading) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="p-4 text-center text-sm text-muted-foreground">
            Loading...
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  if (assistants.length === 0) {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <div className="p-4 text-center text-sm text-muted-foreground">
            No conversations yet
          </div>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  const truncateMessage = (content: string, maxLength: number = 30) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + '...';
  };

  return (
    <TooltipProvider>
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            {displayedAssistants.map((assistant) => {
              const lastMessage = lastMessages[assistant.assistantId];
              const displayContent = lastMessage 
                ? truncateMessage(lastMessage.content) 
                : 'No messages yet';
              const tooltipContent = lastMessage?.content 
                ? truncateMessage(lastMessage.content, 200) 
                : 'Start a conversation';

              return (
                <SidebarMenuItem key={assistant.assistantId} className="my-0.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <SidebarMenuButton asChild className="h-auto py-2">
                        <Link href={assistant.shareUrl || ''} className="flex items-center gap-2 w-full">
                          <Avatar className="h-6 w-6 shrink-0">
                            <AvatarImage src={assistant.avatar} alt={assistant.name} />
                            <AvatarFallback>{assistant.name[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-muted-foreground truncate">
                              {displayContent}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-muted-foreground">
                              <RelativeTime date={assistant.lastAccessedAt} showTooltip={false} />
                            </span>
                            <MessageCircle className="h-3 w-3 text-muted-foreground" />
                          </div>
                        </Link>
                      </SidebarMenuButton>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6 shrink-0">
                            <AvatarImage src={assistant.avatar} alt={assistant.name} />
                            <AvatarFallback>{assistant.name[0]}</AvatarFallback>
                          </Avatar>
                          <p className="font-medium">{assistant.name}</p>
                        </div>
                        <p className="text-sm text-muted-foreground">{tooltipContent}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                    </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        
        {isCollapsible && hasMore && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-2 justify-center"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Show Less
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Show {assistants.length - maxItems} More
              </>
            )}
          </Button>
        )}
      </SidebarGroupContent>
    </SidebarGroup>
    </TooltipProvider>
  );
}

export default EmissaryListMinimal;